"use client";
import {
  LoaderComponent,
  ModalComponent,
  PincodeValidation,
  SnackbarComponent,
} from "@/components";
import ApplicationConfig from "@/components/Wrappers/ApplicationConfig";
import { useRouter } from "@/i18n/routing";
import {
  AMPLITUDE_CODES,
  ConsentType,
  GET_PRODUCT_APPLICATION,
  getPersistentDeviceId,
  IMAGES,
  LOGIN_SCHEMA,
  MOENGAGE_CODES,
  MOENGAGE_EVENTS,
  PRODUCT_HANDLER,
  PRODUCTS,
  STAGE,
  STAGE_BASED_ROUTING,
  SUB_STAGE_FAILS,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { clientId, sessionId } from "@/lib/Firebase";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useOtpTimer from "@/lib/hooks/otpTimerHandler";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useGenerateOtpMutation,
  useGetApplicationQuery,
  useGetCustomerStatusQuery,
  useVerifyOtpMutation,
} from "@/lib/redux";
import {
  useConsentCreateMutation,
  useGetApplicationConfigQuery,
  useGetCustomerQuery,
  usePostUtmDetailsMutation,
  useSetMarketingDataMutation,
} from "@/lib/redux/apis/los-api";
import {
  setAmplitudeIdentifyProperties,
  setAmplitudeUserId,
} from "@/lib/utils/amplitude.utils";
import moengage from "@moengage/web-sdk";
import { Grid } from "@mui/material";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import FaceVerificationModal from "../product-screens/kyc/face-verification/face-verification-modal";
import LeftCard from "./left-card";
import VerifyOtpModalFields from "./login-modal-fields";
import styles from "./login.module.css";
import RightCard from "./right-card";
import { setMoEngageUserAttributes } from "@/lib/Moengage";
import { getFbp } from "@/lib/Meta";

const initialValue = {
  phone: "",
  terms: false,
  whatsapp: false,
};

const ShowOTP = ({ data }) => {
  return (
    process.env.NEXT_PUBLIC_ENVIRONMENT === "dev" &&
    data?.data?.otp && <p>{data?.data?.otp}</p>
  );
};

const LoginScreen = ({ params, afUserId, fbparams, product = "" }) => {
  //HOOKS
  const t = useTranslations("Login");
  const router = useRouter();
  const path = usePathname();

  // Example: set background image based on path
  let backgroundImage = "url('/loginbackground.svg')";
  if (path.includes("medical")) {
    backgroundImage = "url('/medical_loan.svg')";
  } else if (path.includes("consumer")) {
    backgroundImage = "url('/consumer_durables.svg')";
  }
  //STATES
  const [token, setToken] = useState(null);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [openPincodeModal, setOpenPincodeModal] = useState(false);
  const { timeLeft, setTimeLeft, startTimer, resetTimer } = useOtpTimer(60);
  const [showErrorFaceMatchModal, setShowErrorFaceMatchModal] = useState(false);
  const [maintenanceModalVisible, setMaintenanceModalVisible] = useState(false);
  const [lastOtpPhone, setLastOtpPhone] = useState("");
  const [showModalContent, setShowModalContent] = useState({
    label:
      "We would love to serve you in the future, you can re-apply after 30 days.",
    subLabel: "Sorry! Please try later",
    buttonLabel: "OK",
    icon: IMAGES.faceNotVerified,
  });
  //APIS
  const [
    generateOtp,
    {
      data: generateOtpResponse,
      isLoading: otpGenerating,
      isError: otpNotGenerated,
      isSuccess: otpGenerated,
      error: generateError,
    },
  ] = useGenerateOtpMutation();

  const [
    createConsent,
    {
      data: consentData,
      isLoading: consentDataLoading,
      error: errorCreateConsent,
      isSuccess: consentCreated,
    },
  ] = useConsentCreateMutation();
  const [
    verifyOtp,
    {
      data: userData,
      isLoading: otpVerifying,
      isError: verificationError,
      isSuccess: verificationSuccess,
      error: verificationFailedReason,
      reset: resetVerifyOtp,
    },
  ] = useVerifyOtpMutation();
  const [
    postMarketingParams,
    { error: postMarketingError, isSuccess: marketingParamsPosted },
  ] = useSetMarketingDataMutation();
  const {
    data: customerData,
    isLoading: fetchingCustomerData,
    isSuccess: customerDatafetched,
  } = useGetCustomerQuery("", {
    skip: !token || !marketingParamsPosted,
  });
  const {
    data: applicationData,
    isLoading: fetchingApplications,
    error: applicationFetchError,
  } = useGetApplicationQuery("", {
    skip: !token || !customerDatafetched || product === "credit-score",
  });
  const { data: applicationConfigData, refetch: refetchApplicationConfigData } =
    useGetApplicationConfigQuery();

  const {
    data: customerStatus,
    isLoading: customerStatusLoading,
    isError: customerStatusErrors,
    error: customerStatusError,
  } = useGetCustomerStatusQuery("", {
    skip:
      !token ||
      !marketingParamsPosted ||
      !customerDatafetched ||
      product !== "credit-score",
    refetchOnMountOrArgChange: true,
  });

  const serverConfig = applicationConfigData?.data?.serverconfig;
  useEffect(() => {
    if (serverConfig) setMaintenanceModalVisible(serverConfig?.isServerDown);
  }, [serverConfig?.isServerDown]);

  const [postUtmParams] = usePostUtmDetailsMutation();

  //FORMIK
  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: LOGIN_SCHEMA,
    onSubmit: async ({ phone, terms, whatsapp }) => {
      sendEventAnalytics({
        eventName: WEB_EVENTS.ON_LOGIN_SCREEN,
        phone,
      });
      resetVerifyOtp();
      if (
        phone !== lastOtpPhone ||
        (otpGenerated && timeLeft === 0) ||
        !otpGenerated
      ) {
        await generateOtp({ phone, terms, whatsapp });
        setLastOtpPhone(phone);
      }
      setOpenOtpModal(true);
      moengage.trackEvent(MOENGAGE_EVENTS?.VERIFY_OTP_MODAL_OPENED, {
        phone: phone.replace("+91", ""),
      });
    },
  });
  //API COMMON ENITITIES
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCTS.PL,
    applicationData?.data,
  );
  const isLoading =
    otpVerifying ||
    otpGenerating ||
    fetchingCustomerData ||
    fetchingApplications;
  const isError =
    otpNotGenerated ||
    verificationError ||
    applicationFetchError ||
    postMarketingError;
  const errorMessage =
    generateError?.data?.message || verificationFailedReason?.data?.message;
  //FUNCTIONS
  const handleFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      formik.handleChange(e);
    }
  };
  const handleOtpModal = () => {
    setOpenOtpModal(false);
    moengage.trackEvent(MOENGAGE_EVENTS?.VERIFY_OTP_MODAL_CLOSED);
  };

  const handleOtpSubmit = async (otp) => {
    await verifyOtp({
      phone: formik.values.phone,
      otp,
      consent: {
        product_type: PRODUCT_HANDLER[product],
        deviceType: "web",
        deviceId: getPersistentDeviceId(),
        deviceName: `${window?.navigator?.userAgent}`,
        type: ConsentType.LOGIN_CONSENT,
        value: {
          whatsapp: Boolean(formik?.values?.whatsapp),
          terms: Boolean(formik?.values?.terms),
        },
      },
    });
  };

  const handleRouting = () => {
    router.replace(STAGE_BASED_ROUTING(applicationData?.data?.stage));
  };

  //EFFECTS
  useEffect(() => {
    if (otpGenerated) {
      moengage.trackEvent(MOENGAGE_EVENTS?.PHONE_OTP_GENERATED);
      setTimeLeft(
        generateOtpResponse?.data?.timeLeft
          ? generateOtpResponse?.data?.timeLeft
          : 60,
      );
      startTimer();
    }
  }, [otpGenerated]);
  //dispaly errors
  useEffect(() => {
    if (isError) {
      SnackbarComponent({
        error: isError,
        errorMessage: errorMessage,
      });
      // If backend provides a timeLeft, set the timer to that value (do not call startTimer/resetTimer redundantly)
      const newTimeLeft =
        generateError?.data?.timeLeft ||
        verificationFailedReason?.data?.timeLeft;
      if (newTimeLeft) {
        setTimeLeft(Number(newTimeLeft));
      }
      if (errorMessage?.toLowerCase()?.includes("invalid")) {
        moengage?.trackEvent(MOENGAGE_EVENTS?.INVALID_OTP, {
          phone: formik?.values?.phone,
        });
      }
    }
  }, [isError, errorMessage, generateError, verificationFailedReason]);
  //Successful handling of OTP
  useEffect(() => {
    const handleToken = async () => {
      if (userData?.data?.access_token) {
        moengage.identifyUser(`${formik.values?.phone?.replace("+91", "")}`); // UNIQUE_ID is used to uniquely identify a user
        moengage.setMobileNumber(`${formik.values?.phone?.replace("+91", "")}`);
        sessionStorage.token = userData?.data?.access_token;
        const requestData = {
          gaSessionId: sessionId,
          firebaseInstanceId: clientId,
          appsflyerId: afUserId,
          advertisingId: "",
          idfv: "",
          cuid: `${formik.values?.phone}`,
          deviceType: "web",
          deviceId: uuidv4(),
          deviceName: `${window?.navigator?.userAgent}`,
          gaSessionId: sessionId,
          consentType: ConsentType.LOGIN_CONSENT,
          metaInstanceId: getFbp(),
          consentValue: {
            whatsapp: Boolean(formik?.values?.whatsapp),
            terms: Boolean(formik?.values?.terms),
          },
        };
        postMarketingParams(requestData);
        SnackbarComponent({
          success: verificationSuccess,
          successMessage: "Welcome to Hero Fincorp",
        });
        setToken(userData?.data?.access_token);
      }
    };
    handleToken();
  }, [userData?.data?.access_token]);

  //Successful handling of OTP
  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });
  useEffect(() => {
    if (
      customerStatus &&
      (!customerStatus?.data?.panProfileReady ||
        !customerStatus?.data?.hasScore)
    ) {
      router.replace("/credit-score/pan");
    } else if (
      customerStatus?.data?.panProfileReady &&
      customerStatus?.data?.hasScore
    ) {
      router.replace("/credit-score/details");
    }
  }, [customerStatus]);
  useEffect(() => {
    if (userData?.data?.access_token) {
      if (product === "credit-score") {
        return;
      }
      // if (check_eligibility) {
      //   router.push(`/check-eligibility`);
      //   return;
      // }
      const isApplicationClosedOrFailed =
        productApplication?.stage !== undefined &&
        SUB_STAGE_FAILS.includes(productApplication?.subStage);
      if (isApplicationClosedOrFailed) {
        setOpenOtpModal(false);
        setShowErrorFaceMatchModal(true);
        setShowModalContent({
          label: "Sorry! We would love to serve you in the future",
          subLabel:
            productApplication?.loanApplyDate !== null
              ? ""
              : "Please try again later",
          buttonLabel: "OK",
          icon: IMAGES.rejectedLoan,
        });
        return;
      } else if (applicationData?.data?.serviceablePincode) {
        setOpenPincodeModal(true);
      } else if (
        applicationData?.data?.stage === STAGE.APPLY_LOAN ||
        applicationData?.data?.stage === STAGE.LOAN_AMOUNT
      ) {
        router.replace({
          pathname: STAGE_BASED_ROUTING(applicationData?.data?.stage),
          query: params.utmSource ? params : null,
        });
      } else if (productApplication?.stage && customerData?.data?.id) {
        if (params.utmSource) {
          postUtmParams({
            ...params,
            appId: productApplication?.appId,
            customerId: customerData?.data?.id,
            utmInboundUrl: window?.location?.href,
            utmDevice: "web",
          });
        }

        setAmplitudeIdentifyProperties({
          [AMPLITUDE_CODES.IDENTIFIERS.APP_ID]: productApplication?.appId,
          [AMPLITUDE_CODES.IDENTIFIERS.CUSTOMER_ID]: customerData?.data?.id,
        });
        setMoEngageUserAttributes({
          [MOENGAGE_CODES.USER_ATTRIBUTES.APP_ID]: productApplication?.appId,
          [MOENGAGE_CODES.USER_ATTRIBUTES.MOBILE_NUMBER]:
            customerData?.data?.phone,
        });
        if (
          ![
            STAGE.BRE_CPV_TVR,
            STAGE.LMS_ONBOARDING,
            STAGE.CLOSED,
            STAGE.CREDIT_QUEUE,
            STAGE.DISBURSED,
            STAGE.COMPLETED,
          ].includes(productApplication?.stage)
        ) {
          router.replace("/personal-loan/resume-journey");
        } else {
          router.replace("/");
        }
        // router.replace(STAGE_BASED_ROUTING(productApplication?.stage));
      }
      sendEventAnalytics({
        eventName: WEB_EVENTS.SIGNIN_COMPLETED,
        ...customerData?.data,
        ...productApplication,
        utm: fbparams?.source ? fbparams : null,
      });
    }
  }, [applicationData, productApplication]);

  useEffect(() => {
    if (customerData?.data) {
      // const consentRequestData = {
      //   customerId: customerData?.data?.id,
      //   product_type: PRODUCT_HANDLER[product],
      //   deviceType: "web",
      //   deviceId: getPersistentDeviceId(),
      //   deviceName: `${window?.navigator?.userAgent}`,
      //   type: ConsentType.LOGIN_CONSENT,
      // };
      // createConsent(consentRequestData);
      setAmplitudeUserId(customerData?.data?.id);
      AF("pba", "setCustomerUserId", customerData?.data?.id);
    }
  }, [customerDatafetched]);

  // useEffect(() => {
  //   if (typeof window !== "undefined" && window.DeviceIntelligence) {
  //     window.DeviceIntelligence.init({
  //       credentialId: `${process.env.NEXT_PUBLIC_DEVICE_INTELLIGENCE_CREDENTIAL_ID}`,
  //       environment: "PRODUCTION",
  //       enableBehavioralBiometrics: false,
  //       userId: "",
  //       flowId: "Personal_Loan",
  //       clientMetaInfo: {
  //         utm_source: params?.utmSource || "",
  //         product: product || "PL",
  //       },
  //     });
  //     window.DeviceIntelligence.onSubmit((eventId, response) => {
  //       if (response.status === 200) {
  //         console.log("Device intelligence submitted:", eventId);
  //       } else {
  //         console.error("Device intelligence submission failed");
  //       }
  //     });
  //   }
  // }, []);

  // Update userId when phone number is available
  // useEffect(() => {
  //   if (
  //     formik.values.phone &&
  //     typeof window !== "undefined" &&
  //     window.DeviceIntelligence
  //   ) {
  //     window.DeviceIntelligence.setUserId(formik.values.phone);
  //   }
  // }, [formik.values.phone]);

  return (
    <Grid container height={"100%"} sx={{ backgroundColor: "#04251d" }}>
      <LoaderComponent open={isLoading} />
      <ApplicationConfig
        open={maintenanceModalVisible}
        route={handleRouting}
        serverConfig={serverConfig}
      />
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={6}
        sx={{
          backgroundImage,
          backgroundRepeat: "no-repeat",
          backgroundColor: "#04251d",
          backgroundSize: "cover",
        }}
        className={styles["left-card"]}
      >
        <LeftCard {...params} product={product} />
      </Grid>
      <Grid
        bgcolor={"#fff"}
        borderRadius={{
          lg: 0,
          md: 0,
          sm: "20px 20px 0px 0px",
          xs: "20px 20px 0px 0px",
        }}
        item
        xs={12}
        sm={12}
        md={6}
        lg={6}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <RightCard
          formik={formik}
          isError={isError}
          errorMessage={errorMessage}
          handleFieldChange={handleFieldChange}
          product={product}
        />
      </Grid>
      <ModalComponent
        title={"Mobile Verification"}
        open={openOtpModal}
        handleModal={handleOtpModal}
        titleColor={WEB_COLORS.primary}
        style={{ textAlign: "center" }}
      >
        <VerifyOtpModalFields
          formik={formik}
          timeLeft={timeLeft}
          resetTimer={resetTimer}
          generateOtp={generateOtp}
          otpVerifying={otpVerifying}
          handleOtpSubmit={handleOtpSubmit}
          verificationFailedReason={verificationFailedReason}
        />
        {/* <ShowOTP data={generateOtpResponse}/> */}
      </ModalComponent>
      <FaceVerificationModal
        data={showModalContent}
        open={showErrorFaceMatchModal}
        closeModal={() => setShowErrorFaceMatchModal(false)}
        handleModal={() => {
          setShowErrorFaceMatchModal(false);
          router.push("/"); //navigate to login screen
        }}
      />
      <PincodeValidation
        open={openPincodeModal}
        setOpen={setOpenPincodeModal}
        route={handleRouting}
      />
    </Grid>
  );
};

export default LoginScreen;
